import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowDownload16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { ListFilterMenu, ListSearch, ListToolbar } from "@/components/list-toolbar";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { usePageAction } from "@/shell/page-action";
import { todayISO } from "@/engine/date";
import type { ProjectRecord, RecordKind } from "@/engine/types";
import { exportRecordsExcel } from "@/features/records/export-records-excel";
import { RecordAddMenu } from "@/features/records/RecordAddMenu";
import { RecordKindIcon, RecordStatusBadge, SeverityBadge } from "@/features/records/record-ui";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import {
  isRecordOverdue,
  RECORD_KIND_LABEL,
  RECORD_KINDS,
  RECORD_PHASE_LABEL,
  RECORD_SECTIONS,
  recordCode,
  recordPhase,
  RISK_SCALE_LABEL,
  RISK_SCALES,
  riskMatrix,
  riskScore,
  SEVERITY_ORDER,
  SOURCE_LABEL,
  type RecordPhase,
} from "@/store/record-ops";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { useProjectStore } from "@/store/project-store";

type KindFilter = "all" | RecordKind;
type PhaseFilter = "all" | RecordPhase;
const PHASES: RecordPhase[] = ["open", "active", "closed"];
const isDev = process.env.NODE_ENV !== "production";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    padding: `0 ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    height: "44px",
    minHeight: "44px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  titles: { display: "flex", alignItems: "center", minWidth: 0 },
  headerActions: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexShrink: 0 },
  nowrap: { whiteSpace: "nowrap", flexShrink: 0 },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
  },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active td": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  codeCell: {
    width: "1%",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
  },
  codeInner: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  titleCell: {
    maxWidth: 0,
    width: "100%",
    minWidth: "240px",
  },
  closed: { color: tokens.colorNeutralForeground3 },
  shrink: { width: "1%", whiteSpace: "nowrap" },
  overdue: { color: tokens.colorPaletteRedForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
  matrix: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
});

const PHASE_ORDER: Record<RecordPhase, number> = { open: 0, active: 1, closed: 2 };
const SEVERITY_RANK = new Map(SEVERITY_ORDER.map((s, i) => [s, i]));

function sortRecords(list: ProjectRecord[]): ProjectRecord[] {
  return [...list].sort(
    (a, b) =>
      PHASE_ORDER[recordPhase(a.status)] - PHASE_ORDER[recordPhase(b.status)] ||
      (SEVERITY_RANK.get(a.severity!) ?? 9) - (SEVERITY_RANK.get(b.severity!) ?? 9) ||
      (a.due ?? "9999").localeCompare(b.due ?? "9999") ||
      (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0),
  );
}

function matches(r: ProjectRecord, q: string): boolean {
  if (!q) return true;
  const hay = [recordCode(r), r.title, ...r.assignees, ...RECORD_SECTIONS[r.kind].map((d) => r.sections[d.key] ?? "")].join(" ").toLowerCase();
  return hay.includes(q);
}

/** First filled section, shown under the title so the register reads without opening each row. */
function firstLine(r: ProjectRecord): string {
  for (const d of RECORD_SECTIONS[r.kind]) {
    const v = r.sections[d.key]?.trim();
    if (v) return v.split("\n")[0]!;
  }
  return "";
}

export function ProjectRecordsPage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const surface = usePlanSurface();
  const own = useOwnWork();
  const recordKinds = own.restricted ? RECORD_KINDS.filter((k) => k === "issue" || k === "risk" || k === "change") : RECORD_KINDS;
  const [params, setParams] = useSearchParams();
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const records = project.records;
  const today = todayISO();

  const rawKind = params.get("kind");
  const kind: KindFilter = rawKind && (RECORD_KINDS as string[]).includes(rawKind) ? (rawKind as RecordKind) : "all";
  const rawPhase = params.get("phase");
  const phase: PhaseFilter = (PHASES as string[]).includes(rawPhase ?? "") ? (rawPhase as PhaseFilter) : "all";
  const query = params.get("q") ?? "";

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === "") p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  const openId = params.get("open");
  useEffect(() => {
    if (!openId) return;
    if (records.some((r) => r.id === openId)) useProjectStore.getState().setRecordFocus(openId);
    patch({ open: null });
  }, [openId]);

  const ofKind = useMemo(() => (kind === "all" ? records : records.filter((r) => r.kind === kind)), [records, kind]);
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => sortRecords(ofKind.filter((r) => (phase === "all" || recordPhase(r.status) === phase) && matches(r, q))), [ofKind, phase, q]);
  const description = useMemo(() => {
    let open = 0;
    let overdue = 0;
    for (const r of records) {
      if (recordPhase(r.status) !== "open") continue;
      open += 1;
      if (isRecordOverdue(r, today)) overdue += 1;
    }
    return [`${records.length}건`, `처리 필요 ${open}`, overdue > 0 ? `기한 지남 ${overdue}` : ""].filter(Boolean).join(" · ");
  }, [records, today]);
  const phaseCounts = useMemo(() => {
    const m = new Map<RecordPhase, number>();
    for (const r of ofKind) m.set(recordPhase(r.status), (m.get(recordPhase(r.status)) ?? 0) + 1);
    return m;
  }, [ofKind]);
  const add = (k: RecordKind) => useProjectStore.getState().addRecord(k);
  usePageAction(null);
  const open = (id: string) => useProjectStore.getState().setRecordFocus(id);
  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, id: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(id);
    }
  };
  const showKind = kind === "all";
  const showSeverity = kind === "all" || kind === "issue" || kind === "risk" || kind === "change";
  const showScore = kind === "risk";
  const matrix = showScore ? riskMatrix(ofKind) : null;
  const dueHeader = kind === "decision" || kind === "policy" ? "시행일" : kind === "all" ? "기한·시행일" : kind === "change" ? "반영 기한" : "대응 기한";

  return (
    <div className={styles.page}>
      <ListToolbar
        title="이슈"
        description={description}
        headingAction={surface.editIssue ? <RecordAddMenu kinds={recordKinds} onAdd={add} sample={isDev} /> : undefined}
        search={<ListSearch value={query} onChange={(value) => patch({ q: value })} />}
        filters={
          <ListFilterMenu
            label="처리 상태"
            value={phase}
            idleValue="all"
            onChange={(v) => patch({ phase: v === "all" ? null : v })}
            options={[
              { value: "all" as PhaseFilter, label: "전체" },
              ...PHASES.map((p) => ({ value: p as PhaseFilter, label: `${RECORD_PHASE_LABEL[p]} ${phaseCounts.get(p) ?? 0}` })),
            ]}
          />
        }
        trail={
          surface.exportPlan ? (
            <FuiButton className={styles.nowrap} appearance="subtle" icon={<ArrowDownload16Regular />} disabled={!records.length} onClick={() => void exportRecordsExcel(project, schedule)}>
              엑셀 대장
            </FuiButton>
          ) : undefined
        }
        view={
          <SegmentedControl
            value={kind}
            onChange={(v) => patch({ kind: v === "all" ? null : v })}
            ariaLabel="기록 종류"
            options={[
              { value: "all" as KindFilter, label: "전체" },
              ...RECORD_KINDS.map((k) => ({ value: k as KindFilter, label: RECORD_KIND_LABEL[k] })),
            ]}
          />
        }
      />

      <div className={styles.body}>
        {visible.length === 0 ? (
          records.length === 0 ? (
            <GhostEmptyState
              title="아직 기록이 없어요"
              description={surface.editIssue ? "「이슈 추가」로 남기면 대장까지 이어집니다." : "이 플랜에 남은 기록이 없습니다."}
            />
          ) : (
            <GhostEmptyState variant="search" title="조건에 맞는 기록이 없어요" description="종류나 처리 상태를 바꾸거나 검색어를 지워 보세요." />
          )
        ) : (
          <div className={table.frame}>
            {matrix ? (
              <div className={styles.matrix}>
                <Caption1 className={styles.muted}>가능성 × 영향 정도. 종결된 리스크와 점수가 없는 리스크는 빠집니다. 17 이상은 매우 높음입니다.</Caption1>
                <table className={table.table} aria-label="리스크 매트릭스">
                  <thead>
                    <tr>
                      <th className={table.th} scope="col">가능성 \ 영향</th>
                      {RISK_SCALES.map((scale) => (
                        <th key={scale} className={mergeClasses(table.th, table.num)} scope="col">{`${scale} ${RISK_SCALE_LABEL[scale]}`}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RISK_SCALES.map((probability, row) => (
                      <tr key={probability}>
                        <th className={table.th} scope="row">{`${probability} ${RISK_SCALE_LABEL[probability]}`}</th>
                        {RISK_SCALES.map((impact, column) => (
                          <td key={impact} className={mergeClasses(table.td, table.num)}>{matrix[row]?.[column] ? matrix[row]![column] : "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <table className={table.table} aria-label="기록">
              <thead>
                <tr>
                  <th className={table.th}>번호</th>
                  <th className={table.th}>제목</th>
                  <th className={table.th}>상태</th>
                  {showSeverity && <th className={table.th}>심각도</th>}
                  {showScore && <th className={mergeClasses(table.th, table.num)}>점수</th>}
                  <th className={table.th}>출처</th>
                  <th className={table.th}>담당</th>
                  <th className={mergeClasses(table.th, table.num)}>{dueHeader}</th>
                  <th className={mergeClasses(table.th, table.num)}>수정일</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const closed = recordPhase(r.status) === "closed";
                  const overdue = isRecordOverdue(r, today);
                  const sub = firstLine(r);
                  return (
                    <tr key={r.id} className={styles.row} tabIndex={0} onClick={() => open(r.id)} onKeyDown={(e) => onRowKey(e, r.id)} aria-label={`${recordCode(r)} ${r.title} 열기`}>
                      <td className={mergeClasses(table.td, styles.codeCell)}>
                        <span className={styles.codeInner}>
                          {showKind && <RecordKindIcon kind={r.kind} />}
                          {recordCode(r)}
                        </span>
                      </td>
                      <td className={mergeClasses(table.td, styles.titleCell)} title={sub ? `${r.title}\n${sub}` : r.title}>
                        <span className={table.twoLine}>
                          <span className={mergeClasses(table.clip, closed && styles.closed)}>{r.title || "제목 없음"}</span>
                          {sub && <Caption1 className={table.role}>{sub}</Caption1>}
                        </span>
                      </td>
                      <td className={mergeClasses(table.td, styles.shrink)}>
                        <RecordStatusBadge kind={r.kind} status={r.status} />
                      </td>
                      {showSeverity && (
                        <td className={mergeClasses(table.td, styles.shrink)}>
                          {r.kind === "issue" || r.kind === "risk" || r.kind === "change" ? <SeverityBadge severity={r.severity} /> : <span className={styles.muted}>—</span>}
                        </td>
                      )}
                      {showScore && (
                        <td className={mergeClasses(table.td, table.num)}>{riskScore(r) ?? "—"}</td>
                      )}
                      <td className={mergeClasses(table.td, styles.shrink, !r.source && styles.muted)}>{r.source ? SOURCE_LABEL[r.source] : "—"}</td>
                      <td className={mergeClasses(table.td, styles.shrink)} title={r.assignees.join(", ") || "담당 미정"}>
                        <AssigneeAvatars names={r.assignees} />
                      </td>
                      <td className={mergeClasses(table.td, table.num, overdue ? styles.overdue : !r.due && styles.muted)} title={overdue ? "기한 지남" : undefined}>
                        {formatDate(r.due, "numeric")}
                      </td>
                      <td className={mergeClasses(table.td, table.num, styles.muted)} title={formatDate(r.updatedAt, "numericDateTime")}>{formatDate(r.updatedAt, "numeric")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
